import windowsIcon from "../../../../assets/images/windows-0.png";

import { useEffect, useRef, useState } from "react";

import { token } from "../../../../theme";
import { ThemeProvider } from "styled-components";
import { AnimatePresence } from "motion/react";
import {
  BackgroundBottomBar,
  BackgroundBarStartAndInviteContainer,
  BackgroundBottomBarStartButton,
  BackgroundBottomBarStartButtonIcon,
  BackgroundBarInvitationIconContainer,
  BackgroundBarInvitationIcon,
  BackgroundBarInvitationText,
  BackgroundBottomBarTimeContainer,
  BackgroundBottomBarTray,
  BackgroundBottomBarTrayButton,
  StartArea,
} from "./HomeBottomBarStyles";
import { useWindowManager } from "../../utils/WindowManager";
import StartMenu from "../../../../components/StartMenu/StartMenu";
import { playClick } from "../../../../utils/sound";
import { useDesktop } from "../../utils/DesktopContext";
import TrayBalloon from "./TrayBalloon";

const HomeBottomBar = () => {
  const { soundOn, toggleSound } = useDesktop();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [startOpen, setStartOpen] = useState(false);
  const startAreaRef = useRef<HTMLDivElement>(null);
  const formattedDate = currentDateTime.toLocaleDateString("en-GB");

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!startOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (!startAreaRef.current?.contains(e.target as Node)) {
        setStartOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setStartOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [startOpen]);

  const { state, toggleMinimise, bringToFront } = useWindowManager();

  return (
    <ThemeProvider theme={token}>
      <BackgroundBottomBar
        data-taskbar
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 26, delay: 0.1 }}
      >
        <StartArea ref={startAreaRef}>
          <BackgroundBottomBarStartButton
            $pressed={startOpen}
            onClick={() => {
              playClick();
              setStartOpen((open) => !open);
            }}
          >
            <BackgroundBottomBarStartButtonIcon src={windowsIcon} />
            Start
          </BackgroundBottomBarStartButton>
          <StartMenu open={startOpen} onClose={() => setStartOpen(false)} />
        </StartArea>
        <BackgroundBarStartAndInviteContainer>
          <AnimatePresence initial={false}>
            {Array.from(state.windows.values()).map((window) => {
              const isActive =
                !window.isMinimized && state.activeWindowId === window.id;
              return (
                <BackgroundBarInvitationIconContainer
                  key={window.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 30, transition: { duration: 0.12 } }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  onClick={() => {
                    // Windows behaviour: focus a background window, minimise the focused one
                    if (window.isMinimized || isActive) {
                      toggleMinimise(window.id);
                    }
                    if (!isActive) bringToFront(window.id);
                  }}
                  active={isActive}
                >
                  <BackgroundBarInvitationIcon src={window.icon} />
                  <BackgroundBarInvitationText>
                    {window.windowTitle}
                  </BackgroundBarInvitationText>
                </BackgroundBarInvitationIconContainer>
              );
            })}
          </AnimatePresence>
        </BackgroundBarStartAndInviteContainer>

        <BackgroundBottomBarTray>
          <TrayBalloon />
          <BackgroundBottomBarTrayButton
            onClick={toggleSound}
            title={soundOn ? "Mute sounds" : "Unmute sounds"}
          >
            {soundOn ? "🔊" : "🔈"}
          </BackgroundBottomBarTrayButton>
          <BackgroundBottomBarTimeContainer>
            {formattedDate} <br />
            {currentDateTime.toLocaleTimeString()}
          </BackgroundBottomBarTimeContainer>
        </BackgroundBottomBarTray>
      </BackgroundBottomBar>
    </ThemeProvider>
  );
};

export default HomeBottomBar;
